/**
 * `ComposeWizard` — the guided "Ota yhteyttä" wizard shell (027 US6, D-120;
 * PR-1 frame + step-0 chain, PR-2 per-field question steps).
 *
 * The app's ONE contact experience (FR-023): every entry (shell nav, Messages
 * FAB, Home quick-access, content-surface CTAs) opens this modal through
 * `ComposeWizardProvider`. Renders inside the project Dialog primitive with
 * `variant="wizard"` (the proto's guided-wizard modal: viewport-filling on
 * mobile, a fixed-height 85dvh / 800px-cap / 600px card at `md:`, a flex
 * column that does NOT scroll itself — the owner fold at PR-3).
 *
 * Step machine (D-140: BOTH channels enter the shared service selector):
 *
 *   channel ──"Lähetä viesti"/"Soita"──▶ service-line ──┬─ call ───▶ soita-info
 *                                                       │           (service-specific
 *                                                       │            static call card)
 *                                                       └─ message ─▶ topic ──▶ questions
 *                                                                   (WizardQuestionFlow:
 *                                                                    one catalogue field
 *                                                                    per step, reveal
 *                                                                    projection, dead-ends,
 *                                                                    the send step —
 *                                                                    PR-2, T059-T061)
 *
 *   - `channel`, `service-line`, `topic` are auto-advance card-choice steps
 *     (one choice per step, FR-024). The `channel` choice (message/call) is
 *     held in local state and selects the `service-line` exit.
 *   - `soita-info` is channel presentation ONLY: no data flow, no send, and is
 *     service-specific (D-140): Lakikaveri reads 0200 55011 / ma-to klo 9-14
 *     with the full bullet run; jäsenpalvelu reads 02 760 7630 / ma-pe klo 9-11
 *     with a slimmer run. The step body lives in `SoitaInfoStep.tsx`.
 *   - `questions` owns its OWN top-bar/back handling (the flow's back walks
 *     its internal steps; back past the first field returns here to `topic`).
 *     Topic re-selection remounts the flow (`key={topicKey}`) so a changed
 *     topic starts clean — the shipped FR-002 reset semantics.
 *
 * Close (FR-025 / FR-010, the review's LOW-3 reading): the abandon-confirm
 * ("Oletko varma?") is keyed on ENTERED DATA — ≥1 answered field (or a chosen
 * attachment) reported by the question flow. Selections alone (channel,
 * service line, topic) discard nothing and close directly, as does the
 * static soita-info path and a post-success close.
 *
 * Scroll-to-top: the shell's `WizardScrollArea` (pinned between the top bar
 * and any bottom bar) is the scroll container for the step-0 chain; every
 * OUTER step change resets its position (FR-024). The question flow owns its
 * own scroll area and resets it on its internal transitions.
 */

import { type JSX, startTransition, useEffect, useId, useRef, useState } from 'react';

import { useTranslate } from '../../../i18n/useTranslate.js';
import { newResignationIntent } from '../../../membership-resignation/MembershipResignationPort.js';
import { Button } from '../../../primitives/Button.js';
import { Dialog } from '../../../primitives/Dialog.js';
import { ChatBubbleIcon, PhoneIcon, ScaleIcon, UserCircleIcon } from '../../../shell/icons.js';
import { assertNever } from '../../../shell/MenuSheetHelpers.js';
import type { ServiceLine, TopicDefinition } from '../MessageComposePort.js';
import { useComposePort } from '../useComposePort.js';
import { ContactChoiceCard } from './ContactChoiceCard.js';
import {
  choiceCardClasses,
  choiceListClasses,
  choiceTitleClasses,
  entryColumnClasses,
  entryListClasses,
  stepColumnClasses,
  stepNoticeClasses,
  WizardBottomBar,
  WizardScrollArea,
  WizardTopBar,
} from './chrome.js';
import { type ResignationPhase, ResignationStep } from './ResignationStep.js';
import { SoitaInfoStep } from './SoitaInfoStep.js';
import { useWizardViewport } from './useWizardViewport.js';
import { WizardQuestionFlow } from './WizardQuestionFlow.js';

export interface ComposeWizardProps {
  readonly isOpen: boolean;
  /** Invoked when the wizard actually closes (after any abandon-confirm). */
  readonly onClose: () => void;
  readonly onCloseAutoFocus?: (event: Event) => void;
}

type WizardStep = 'channel' | 'soita-info' | 'service-line' | 'topic' | 'questions' | 'resignation';

/**
 * The chosen contact channel (proto step 0). Both channels now share the
 * service selector (step 'service-line'); the channel decides where a service
 * choice routes — call → the service-specific `soita-info` card, message → the
 * topic flow.
 */
type ContactChannel = 'message' | 'call';

/** Stable service-line order: Lakikaverille first (mirrors the shipped selector). */
const SERVICE_LINES: readonly ServiceLine[] = ['viesti_lakikaverille', 'viesti_jasenpalveluun'];

// Service-line card titles per channel (proto step 2): the message variant
// reuses the surface titles ("Viesti Lakikaverille" / "Viesti jäsenpalveluun");
// the call variant reads "Soita …" (proto's `contactFlowType` title switch).
const SERVICE_LINE_TITLE_KEYS = {
  viesti_lakikaverille: 'compose.title',
  viesti_jasenpalveluun: 'compose.titleJasenpalvelu',
} as const satisfies Record<ServiceLine, string>;

const SERVICE_LINE_CALL_TITLE_KEYS = {
  viesti_lakikaverille: 'compose.wizard.serviceLine.call.lakikaveri',
  viesti_jasenpalveluun: 'compose.wizard.serviceLine.call.jasenpalvelu',
} as const satisfies Record<ServiceLine, string>;

// Service-card subtitle line, shown under the title in BOTH channel variants
// (proto step 2: each card carries a one-line matter description).
const SERVICE_LINE_SUBTITLE_KEYS = {
  viesti_lakikaverille: 'compose.wizard.serviceLine.lakikaveri.subtitle',
  viesti_jasenpalveluun: 'compose.wizard.serviceLine.jasenpalvelu.subtitle',
} as const satisfies Record<ServiceLine, string>;

// Service-line card glyphs: scales = legal/employment (Lakikaveri), user-circle
// = membership (jäsenpalvelu); proto-aligned at D-140 (owner-approved
// 2026-06-25). UserCircleIcon is hand-authored inline (D-043/R-002 — no
// lucide-react). Glyphs render on the service-line step (both channel variants)
// and OFF the topic step — visual-map ledger row 18.
const SERVICE_LINE_ICONS = {
  viesti_lakikaverille: ScaleIcon,
  viesti_jasenpalveluun: UserCircleIcon,
} as const satisfies Record<ServiceLine, typeof ScaleIcon>;

export function ComposeWizard({
  isOpen,
  onClose,
  onCloseAutoFocus,
}: ComposeWizardProps): JSX.Element {
  const t = useTranslate();
  const viewportRef = useWizardViewport(isOpen);
  const composePort = useComposePort();
  const [step, setStep] = useState<WizardStep>('channel');
  const [channel, setChannel] = useState<ContactChannel | null>(null);
  const [serviceLine, setServiceLine] = useState<ServiceLine | null>(null);
  const [topicKey, setTopicKey] = useState<string | null>(null);
  const [topics, setTopics] = useState<readonly TopicDefinition[]>([]);
  const [confirmingAbandon, setConfirmingAbandon] = useState(false);
  // FR-025 (LOW-3): true once the question flow reports ≥1 answered field.
  const [flowDirty, setFlowDirty] = useState(false);
  const [resignationPhase, setResignationPhase] = useState<ResignationPhase>('editing');
  // Lives here, not in the step, so it survives leaving and re-entering the
  // step until the wizard is reset.
  const [resignationIntent, setResignationIntent] = useState(newResignationIntent);
  const resignationLiveId = useId();
  // The SHELL's scroll area (the step-0 chain). The question flow owns its
  // own scroll area + reset; this ref never targets it.
  const shellScrollRef = useRef<HTMLDivElement>(null);

  // Load the catalogue once per open for the topic step. Same cancellation
  // posture as the inline flow's load (R13).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let active = true;
    void composePort
      .listTopics()
      .then((loaded) => {
        if (active) {
          setTopics(loaded);
        }
      })
      .catch((error: unknown) => {
        // A rejected catalogue load leaves the topic step empty rather than
        // surfacing an unhandled rejection; logged for diagnosis (the demo
        // adapter never rejects — this guards a future real adapter).
        // biome-ignore lint/suspicious/noConsole: observability for a rejected catalogue load with no error UI in scope
        console.error('compose wizard: topic catalogue load failed', error);
      });
    return () => {
      active = false;
    };
  }, [composePort, isOpen]);

  // scroll-to-top idiom: the shell's scroll area is the scroll container for
  // the step-0 chain; every OUTER step change resets its position (the mount
  // run is skipped via the ref — the dialog opens at the top anyway). The
  // question flow's internal per-field transitions do NOT change `step` (it
  // stays 'questions') — the flow resets its OWN scroll area on those.
  const previousStepRef = useRef<WizardStep>('channel');
  useEffect(() => {
    if (previousStepRef.current !== step) {
      previousStepRef.current = step;
      shellScrollRef.current?.scrollTo({ top: 0 });
    }
  }, [step]);

  // Step autofocus (owner UX nit r2): the rendered selection list's FIRST
  // card takes focus per outer step change; other steps own their focus.
  // Leaving the resignation step returns focus to its own card, not the first.
  const firstCardRef = useRef<HTMLButtonElement>(null);
  const resignationCardRef = useRef<HTMLButtonElement>(null);
  const focusStepRef = useRef<WizardStep>('channel');
  useEffect(() => {
    const from = focusStepRef.current;
    focusStepRef.current = step;
    if (!isOpen || step === 'questions' || step === 'soita-info' || step === 'resignation') {
      return;
    }
    if (step === 'topic' && from === 'resignation' && resignationCardRef.current !== null) {
      resignationCardRef.current.focus();
      return;
    }
    firstCardRef.current?.focus();
  }, [step, isOpen]);

  const resetAndClose = (): void => {
    setStep('channel');
    setChannel(null);
    setServiceLine(null);
    setTopicKey(null);
    setConfirmingAbandon(false);
    setFlowDirty(false);
    setResignationPhase('editing');
    setResignationIntent(newResignationIntent());
    // 052 FR-004: the actual close is the SINGLE seam every close path converges
    // on (the X and Sulje buttons, Escape, and the abandon-confirm accept).
    // Backdrop/outside clicks are intentionally non-dismissible (see
    // `onInteractOutside` preventDefault below), so there is no backdrop close
    // path. After a successful send the list cache was evicted, so closing
    // re-renders the still-mounted `CasesList` (compose is an overlay) and it
    // re-reads → suspends. Wrapping the close in a transition keeps the current
    // UI committed until the fresh read resolves, so the mounted list is never
    // torn down to `CasesLoading` with focus dropped to `<body>`. On closes that
    // did not evict (no send / abandon) the update does not suspend, so the
    // transition is a harmless no-op — which is why it lives here, covering every
    // path, rather than on individual buttons (research Decision 1; contracts §1).
    startTransition(onClose);
  };

  const requestClose = (): void => {
    if (step === 'resignation' && resignationPhase === 'submitting') {
      return;
    }
    if (flowDirty) {
      setConfirmingAbandon(true);
      return;
    }
    resetAndClose();
  };

  const goBack = (): void => {
    switch (step) {
      case 'soita-info':
        // The call card sits one step past the service selector; back returns
        // to it (keeping the chosen channel) so another service can be picked.
        setServiceLine(null);
        setStep('service-line');
        return;
      case 'service-line':
        setChannel(null);
        setServiceLine(null);
        setStep('channel');
        return;
      case 'topic':
        setTopicKey(null);
        setStep('service-line');
        return;
      case 'resignation':
        setResignationPhase('editing');
        setStep('topic');
        return;
      case 'questions':
        // Unreachable from the shell top-bar: the question flow renders its
        // own top-bar and owns back inside the flow (onExitToTopics returns
        // here). Kept for exhaustiveness.
        setStep('topic');
        return;
      case 'channel':
        return;
      default:
        // Exhaustiveness guard: a new WizardStep without a back rule fails
        // the compile here.
        assertNever(step);
    }
  };

  const lineTopics = topics.filter((topic) => topic.serviceLine === serviceLine);
  const activeTopic =
    topicKey === null ? undefined : topics.find((topic) => topic.key === topicKey);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          requestClose();
        }
      }}
    >
      <Dialog.Content
        variant="wizard"
        ref={viewportRef}
        onCloseAutoFocus={onCloseAutoFocus}
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          // While the calendar popover is open, Escape belongs to IT — by
          // PRESENCE, not event target: the date input keeps focus during
          // typing, so a target check would miss input-focused escapes. The
          // popover renders the marker only while open and owns its own
          // document-level close listener (MED-2).
          if (document.querySelector('[data-calendar-popover]') !== null) {
            return;
          }
          requestClose();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        onKeyDown={(event) => {
          // Autorepeat guard: a HELD Enter must not chain-activate the
          // autofocused card on each new step; discrete presses untouched.
          if (event.key === 'Enter' && event.repeat) {
            event.preventDefault();
          }
        }}
      >
        {step !== 'questions' && (
          <WizardTopBar
            onBack={
              step === 'channel' || (step === 'resignation' && resignationPhase !== 'editing')
                ? undefined
                : goBack
            }
            onClose={requestClose}
            closeUnavailableDescribedBy={
              step === 'resignation' && resignationPhase === 'submitting'
                ? resignationLiveId
                : undefined
            }
          />
        )}

        {/* The step-0 chain shares ONE persistent scroll area between the
            pinned bars; soita-info's bottom bar renders BELOW it. */}
        {step !== 'questions' && (
          <WizardScrollArea
            ref={shellScrollRef}
            variant={
              step === 'channel' || step === 'service-line'
                ? 'choice'
                : step === 'soita-info'
                  ? 'call'
                  : 'default'
            }
          >
            {step === 'channel' && (
              <div className={entryColumnClasses}>
                <Dialog.Title size="wizard">{t('compose.wizard.channel.title')}</Dialog.Title>
                <div className={entryListClasses}>
                  <ContactChoiceCard
                    ref={firstCardRef}
                    title={t('compose.wizard.channel.send.title')}
                    icon={ChatBubbleIcon}
                    onClick={() => {
                      setChannel('message');
                      setStep('service-line');
                    }}
                  />
                  <ContactChoiceCard
                    title={t('compose.wizard.channel.call.title')}
                    icon={PhoneIcon}
                    onClick={() => {
                      setChannel('call');
                      setStep('service-line');
                    }}
                  />
                </div>
              </div>
            )}

            {step === 'soita-info' && <SoitaInfoStep serviceLine={serviceLine} />}

            {step === 'service-line' && (
              <div className={entryColumnClasses}>
                {/* Heading-only step (proto step 2): the "Asiani liittyy…" lead
                    was removed at D-140 (owner-approved 2026-06-25). */}
                <Dialog.Title size="wizard">{t('compose.wizard.serviceLine.title')}</Dialog.Title>
                <div className={entryListClasses}>
                  {SERVICE_LINES.map((line, index) => {
                    const LineIcon = SERVICE_LINE_ICONS[line];
                    // Channel-aware title: "Soita …" in the call branch,
                    // "Viesti …" in the message branch (proto step 2).
                    const titleKey =
                      channel === 'call'
                        ? SERVICE_LINE_CALL_TITLE_KEYS[line]
                        : SERVICE_LINE_TITLE_KEYS[line];
                    return (
                      <ContactChoiceCard
                        key={line}
                        ref={index === 0 ? firstCardRef : undefined}
                        title={t(titleKey)}
                        description={t(SERVICE_LINE_SUBTITLE_KEYS[line])}
                        icon={LineIcon}
                        onClick={() => {
                          setServiceLine(line);
                          setStep(channel === 'call' ? 'soita-info' : 'topic');
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {step === 'topic' && (
              <div className={stepColumnClasses}>
                <Dialog.Title size="wizard">{t('compose.wizard.topic.title')}</Dialog.Title>
                {/* Verbatim callback-request PREFIX at the START of the
                    Lakikaverille flow (T067; the doc's Huomio 1 'alussa' —
                    visual-map ledger row 12 as amended): displayed above the
                    topic cards, REUSING the row-19 notice idiom. The
                    confirmation-overview display stays (owner-approved);
                    the FR-007 payload prepend is untouched. */}
                {serviceLine === 'viesti_lakikaverille' ? (
                  <p className={stepNoticeClasses}>{t('compose.prefix')}</p>
                ) : null}
                <div className={choiceListClasses}>
                  {lineTopics.map((topic, index) => (
                    <button
                      key={topic.key}
                      ref={index === 0 ? firstCardRef : undefined}
                      type="button"
                      className={choiceCardClasses}
                      onClick={() => {
                        setTopicKey(topic.key);
                        setStep('questions');
                      }}
                    >
                      <span className={choiceTitleClasses}>{t(topic.label)}</span>
                    </button>
                  ))}
                  {/* 056: NOT a catalogue topic (D-235) — sourced separately
                      from lineTopics, and only on the jäsenpalvelu line. */}
                  {serviceLine === 'viesti_jasenpalveluun' ? (
                    <button
                      ref={resignationCardRef}
                      type="button"
                      className={choiceCardClasses}
                      onClick={() => setStep('resignation')}
                    >
                      <span className={choiceTitleClasses}>
                        {t('compose.wizard.resignation.card')}
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            {step === 'resignation' && (
              <ResignationStep
                onCancel={() => {
                  setResignationPhase('editing');
                  setStep('topic');
                }}
                onClose={resetAndClose}
                onPhaseChange={setResignationPhase}
                intent={resignationIntent}
                liveRegionId={resignationLiveId}
              />
            )}
          </WizardScrollArea>
        )}

        {/* soita-info's Sulje is a pinned bar BELOW the scroll area. */}
        {step === 'soita-info' && (
          <WizardBottomBar label={t('compose.wizard.close')} onClick={resetAndClose} />
        )}

        {step === 'questions' && activeTopic !== undefined && (
          <WizardQuestionFlow
            key={activeTopic.key}
            topic={activeTopic}
            onExitToTopics={() => {
              setTopicKey(null);
              setFlowDirty(false);
              setStep('topic');
            }}
            requestClose={requestClose}
            onDirtyChange={setFlowDirty}
          />
        )}
      </Dialog.Content>

      {/* abandon-confirm idiom (FR-025): a small centred confirm dialog over
          the wizard. Controlled locally; "Kyllä" discards + closes, "Peruuta"
          returns to the flow untouched. */}
      <Dialog.Root open={confirmingAbandon} onOpenChange={setConfirmingAbandon}>
        <Dialog.Content aria-describedby={undefined}>
          <div className="flex flex-col gap-3 text-center">
            <Dialog.Title>{t('compose.wizard.abandon.title')}</Dialog.Title>
            <p className="m-0 text-sm text-muted-foreground">{t('compose.wizard.abandon.body')}</p>
            <Button type="button" variant="outline" onClick={resetAndClose}>
              {t('compose.wizard.abandon.confirm')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setConfirmingAbandon(false)}>
              {t('compose.wizard.abandon.cancel')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </Dialog.Root>
  );
}
